/**
 * LibraryView - Card library with compact swipeable cards
 *
 * Features:
 * - Page-based pagination (mobile-first with visible page controls)
 * - Server-side search across ALL cards (debounced 300ms)
 * - Swipe right → "View" action (expand to full detail card)
 * - Swipe left → "Delete" action (with confirmation)
 * - Inline card editing from detail view
 * - Pronunciation buttons (US/UK)
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { getAllCards, deleteCard, updateCard, setCardMastered, getMasteredCards } from '@/lib/api-client'
import { getImageUrl } from '@/lib/image-utils'
import { PronunciationButton } from '@/components/PronunciationButton'
import { getEnglishText } from '@/lib/tts'
import type { CardResponse } from '@shared/schemas'

type Card = CardResponse

type SwipeState = 'normal' | 'view' | 'delete'
type LibraryFilter = 'all' | 'learning' | 'mastered'

const SWIPE_THRESHOLD = 60
const DIRECTION_LOCK_THRESHOLD = 10
const SEARCH_DEBOUNCE_MS = 300
const PAGE_SIZE = 20

export function LibraryView() {
  const [cards, setCards] = useState<Card[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [totalCards, setTotalCards] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const [filter, setFilter] = useState<LibraryFilter>('all')
  const [masteredCount, setMasteredCount] = useState(0)
  // Total count for the "all" tab — persisted across filter switches
  const [allCardsTotal, setAllCardsTotal] = useState(0)
  const [masteredIds, setMasteredIds] = useState<Set<string>>(new Set())
  const [togglingMasteredId, setTogglingMasteredId] = useState<string | null>(null)

  // Expanded card (detail view)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Edit state (within expanded detail view)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editedCard, setEditedCard] = useState<Card | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [updateError, setUpdateError] = useState<string | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  // Refs for search debouncing
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchSeqRef = useRef(0)
  const lastFetchedKey = useRef('')
  const searchTermRef = useRef(searchTerm)
  searchTermRef.current = searchTerm

  // ── Fetch cards based on current filter + page ──
  const fetchByFilter = useCallback(async (searchOverride?: string, pageOverride?: number) => {
    const searchVal = searchOverride ?? searchTermRef.current
    const pageVal = pageOverride ?? 1

    if (filter === 'mastered') {
      const response = await getMasteredCards({
        limit: PAGE_SIZE,
        search: searchVal || undefined,
        page: pageVal,
      })
      return {
        cards: response.cards || [],
        hasMore: response.hasMore ?? false,
        total: response.total ?? 0,
      }
    }

    const response = await getAllCards({
      limit: PAGE_SIZE,
      search: searchVal || undefined,
      mastered: filter === 'learning' ? false : undefined,
      page: pageVal,
    })
    return {
      cards: response.cards || [],
      hasMore: response.hasMore ?? false,
      total: response.total ?? 0,
    }
  }, [filter])

  // ── Initial load + filter change ──
  const fetchInitial = useCallback(async () => {
    setLoading(true)
    setError(null)
    lastFetchedKey.current = `${filter}:${searchTermRef.current}`
    try {
      const data = await fetchByFilter()
      setCards(data.cards)
      setTotalCards(data.total)
      setTotalPages(Math.max(1, Math.ceil(data.total / PAGE_SIZE)))
      setPage(1)
      setInitialLoadDone(true)

      // Fetch mastered count for tab display (always needed)
      try {
        const masteredResp = await getMasteredCards({ limit: 1 })
        const mCount = masteredResp.total ?? 0
        setMasteredCount(mCount)

        // When on "all" tab, data.total already includes mastered cards
        // so allCardsTotal = data.total
        if (filter === 'all') {
          setAllCardsTotal(data.total)
        } else if (filter === 'learning') {
          // learning total + mastered count = all cards total
          setAllCardsTotal(prev => prev || data.total + mCount)
        }
        // On "mastered" tab, allCardsTotal stays as-is
      } catch { /* non-critical */ }

      // Track mastered IDs for visual indicator on "all" tab
      if (filter === 'all') {
        try {
          const masteredResp = await getMasteredCards({ limit: 100 })
          setMasteredIds(new Set(masteredResp.cards.map(c => c.id)))
        } catch { /* non-critical */ }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cards')
    } finally {
      setLoading(false)
    }
  }, [fetchByFilter, filter])

  useEffect(() => {
    setInitialLoadDone(false)
    setExpandedId(null)
    setEditingId(null)
    fetchInitial()
  }, [fetchInitial, filter])

  // ── Server-side search (debounced) ──
  useEffect(() => {
    if (!initialLoadDone) return

    const key = `${filter}:${searchTerm}`
    if (lastFetchedKey.current === key) return

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }

    searchTimerRef.current = setTimeout(async () => {
      const seq = ++searchSeqRef.current
      try {
        const data = await fetchByFilter(searchTerm || undefined)
        if (seq !== searchSeqRef.current) return
        setCards(data.cards)
        setTotalCards(data.total)
        setTotalPages(Math.max(1, Math.ceil(data.total / PAGE_SIZE)))
        setPage(1)
      } catch {
        // Silently fail search
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, initialLoadDone, filter])

  // ── Page change ──
  const goToPage = useCallback(async (newPage: number) => {
    setLoading(true)
    try {
      const data = await fetchByFilter(undefined, newPage)
      setCards(data.cards)
      setTotalCards(data.total)
      setTotalPages(Math.max(1, Math.ceil(data.total / PAGE_SIZE)))
      setPage(newPage)
      setExpandedId(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cards')
    } finally {
      setLoading(false)
    }
  }, [fetchByFilter])

  // ── Refresh current page after mutation ──
  const refreshCards = useCallback(async () => {
    try {
      const data = await fetchByFilter(undefined, page)
      setCards(data.cards)
      setTotalCards(data.total)
      setTotalPages(Math.max(1, Math.ceil(data.total / PAGE_SIZE)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cards')
    }
  }, [fetchByFilter, page])

  // Edit handlers
  const handleEdit = useCallback((card: Card) => {
    setEditingId(card.id)
    setEditedCard({ ...card })
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingId(null)
    setEditedCard(null)
  }, [])

  const handleEditFieldChange = useCallback((field: keyof Card, value: string) => {
    setEditedCard(prev => (prev ? { ...prev, [field]: value } : null))
  }, [])

  const handleSaveEdit = useCallback(async (cardId: string) => {
    if (!editedCard || savingId) return

    const updateData: Record<string, string> = {}
    const fields: (keyof Card)[] = ['front', 'back', 'definition', 'example', 'pos', 'phonetic_us', 'phonetic_uk', 'type']
    for (const field of fields) {
      const value = editedCard[field]
      if (typeof value === 'string' && value.trim() !== '') {
        updateData[field] = value
      }
    }

    if (Object.keys(updateData).length === 0) {
      setEditingId(null)
      setEditedCard(null)
      return
    }

    setSavingId(cardId)
    setUpdateError(null)
    try {
      const response = await updateCard(cardId, updateData)
      if (response.success) {
        await refreshCards()
        setEditingId(null)
        setEditedCard(null)
      }
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update card')
    } finally {
      setSavingId(null)
    }
  }, [editedCard, savingId, refreshCards])

  // Delete handler
  const handleDelete = useCallback(async (cardId: string) => {
    if (deletingId) return

    setDeletingId(cardId)
    setDeleteError(null)
    setPendingDeleteId(null)
    try {
      await deleteCard(cardId)
      setAllCardsTotal(prev => Math.max(0, prev - 1))
      await refreshCards()
      setExpandedId(null)
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete card')
    } finally {
      setDeletingId(null)
    }
  }, [deletingId, refreshCards])

  // Mastered toggle handler
  const handleToggleMastered = useCallback(async (cardId: string, currentlyMastered: boolean) => {
    if (togglingMasteredId) return
    setTogglingMasteredId(cardId)
    try {
      await setCardMastered(cardId, !currentlyMastered)
      if (filter === 'mastered' || filter === 'learning') {
        await refreshCards()
        setExpandedId(null)
      } else {
        setMasteredIds(prev => {
          const next = new Set(prev)
          if (currentlyMastered) next.delete(cardId)
          else next.add(cardId)
          return next
        })
        setMasteredCount(c => currentlyMastered ? Math.max(0, c - 1) : c + 1)
      }
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to update mastered status')
    } finally {
      setTogglingMasteredId(null)
    }
  }, [togglingMasteredId, filter, refreshCards])

  // ---- Render states ----

  if (loading && cards.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <span className="material-symbols-outlined text-5xl text-primary animate-spin">progress_activity</span>
          <p className="text-on-surface-variant mt-4">Loading library...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
        <span className="material-symbols-outlined text-5xl text-error mb-4">error</span>
        <p className="text-on-surface text-center mb-4">{error}</p>
        <button onClick={fetchInitial} className="px-6 py-3 bg-primary text-on-primary rounded-full font-semibold">
          Retry
        </button>
      </div>
    )
  }

  if (cards.length === 0 && !searchTerm && filter === 'all') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
        <span className="material-symbols-outlined text-6xl text-primary mb-4">library_books</span>
        <h2 className="font-headline font-bold text-xl text-on-surface mb-2">No cards yet!</h2>
        <p className="text-on-surface-variant text-center">
          Generate some flashcards to build your library.
        </p>
      </div>
    )
  }

  const learningCount = allCardsTotal - masteredCount

  return (
    <div className="px-4 py-4 flex flex-col gap-4">
      {/* Delete error */}
      {deleteError && (
        <div className="bg-error-container rounded-2xl p-3 flex items-center gap-2">
          <span className="material-symbols-outlined text-error text-[18px]">error</span>
          <p className="text-sm text-on-error-container flex-1">{deleteError}</p>
          <button onClick={() => setDeleteError(null)} className="text-xs font-bold text-on-error-container">
            Dismiss
          </button>
        </div>
      )}

      {/* Update error */}
      {updateError && (
        <div className="bg-error-container rounded-2xl p-3 flex items-center gap-2">
          <span className="material-symbols-outlined text-error text-[18px]">error</span>
          <p className="text-sm text-on-error-container flex-1">{updateError}</p>
          <button onClick={() => setUpdateError(null)} className="text-xs font-bold text-on-error-container">
            Dismiss
          </button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {pendingDeleteId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-dialog-title"
        >
          <div className="bg-surface-container-lowest rounded-2xl p-6 mx-4 max-w-sm w-full shadow-card">
            <h3 id="delete-dialog-title" className="font-headline font-bold text-lg text-on-surface mb-2">Delete Card?</h3>
            <p className="text-sm text-on-surface-variant mb-4">
              This action cannot be undone. The card will be permanently removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingDeleteId(null)}
                className="flex-1 py-2.5 bg-surface-container text-on-surface rounded-full font-semibold text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(pendingDeleteId)}
                disabled={deletingId === pendingDeleteId}
                className="flex-1 py-2.5 bg-error text-on-error rounded-full font-semibold text-sm"
              >
                {deletingId === pendingDeleteId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter tabs — counts always based on allCardsTotal (set on "all" tab load) */}
      <div className="flex gap-1 bg-surface-container rounded-2xl p-1">
        {([
          { key: 'all' as LibraryFilter, label: 'All', count: allCardsTotal },
          { key: 'learning' as LibraryFilter, label: 'Learning', count: learningCount },
          { key: 'mastered' as LibraryFilter, label: 'Mastered', count: masteredCount },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => { setFilter(tab.key); setSearchTerm('') }}
            className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold transition-all ${
              filter === tab.key
                ? 'bg-primary text-on-primary shadow-sm'
                : 'text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            {tab.label}
            <span className="ml-1 opacity-70">{tab.count > 0 ? tab.count : ''}</span>
          </button>
        ))}
      </div>

      {/* Search + count header */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-[20px]">
            search
          </span>
          <input
            type="text"
            placeholder={`Search ${filter === 'mastered' ? 'mastered' : filter === 'learning' ? 'learning' : ''} cards...`}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-surface-container-lowest rounded-2xl text-on-surface text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <span className="text-xs font-bold text-on-surface-variant whitespace-nowrap">
          {searchTerm ? `${totalCards} matches` : `${totalCards} cards`}
        </span>
      </div>

      {/* Paginator — above the card list so it's visible without scrolling */}
      {totalPages > 1 && (
        <Paginator
          currentPage={page}
          totalPages={totalPages}
          onPageChange={goToPage}
          disabled={loading}
        />
      )}

      {/* Card list */}
      <div className="flex flex-col gap-2">
        {cards.length === 0 && searchTerm && (
          <p className="text-center text-on-surface-variant py-8">No cards match &ldquo;{searchTerm}&rdquo;</p>
        )}
        {cards.length === 0 && !searchTerm && filter === 'mastered' && (
          <div className="flex flex-col items-center py-12">
            <span className="material-symbols-outlined text-4xl text-on-surface-variant mb-2">emoji_events</span>
            <p className="text-sm text-on-surface-variant text-center">No mastered words yet.<br/>Mark cards as mastered to see them here.</p>
          </div>
        )}
        {cards.length === 0 && !searchTerm && filter === 'learning' && (
          <div className="flex flex-col items-center py-12">
            <span className="material-symbols-outlined text-4xl text-on-surface-variant mb-2">school</span>
            <p className="text-sm text-on-surface-variant text-center">All your cards are mastered!<br/>Great job!</p>
          </div>
        )}

        {loading && cards.length > 0 && (
          <div className="flex justify-center py-2">
            <span className="material-symbols-outlined text-2xl text-primary animate-spin">progress_activity</span>
          </div>
        )}

        {cards.map((card, index) => (
          expandedId === card.id ? (
            <ExpandedCard
              key={card.id}
              card={card}
              isEditing={editingId === card.id}
              editedCard={editedCard}
              savingId={savingId}
              isMastered={masteredIds.has(card.id) || filter === 'mastered'}
              togglingMastered={togglingMasteredId === card.id}
              onEdit={() => handleEdit(card)}
              onCancelEdit={handleCancelEdit}
              onEditFieldChange={handleEditFieldChange}
              onSaveEdit={() => handleSaveEdit(card.id)}
              onCollapse={() => setExpandedId(null)}
              onToggleMastered={() => handleToggleMastered(card.id, masteredIds.has(card.id) || filter === 'mastered')}
            />
          ) : (
            <SwipeableLibraryCard
              key={card.id}
              card={card}
              colorIndex={index}
              isMastered={masteredIds.has(card.id) || filter === 'mastered'}
              onView={() => setExpandedId(card.id)}
              onDelete={() => setPendingDeleteId(card.id)}
            />
          )
        ))}
      </div>
    </div>
  )
}

// ─── Paginator ──────────────────────────────────────────────────────

interface PaginatorProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  disabled?: boolean
}

function Paginator({ currentPage, totalPages, onPageChange, disabled }: PaginatorProps) {
  // Calculate visible page numbers (show at most 5 pages around current)
  const getVisiblePages = () => {
    const pages: (number | 'ellipsis')[] = []

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
      return pages
    }

    pages.push(1)

    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)

    if (start > 2) pages.push('ellipsis')

    for (let i = start; i <= end; i++) pages.push(i)

    if (end < totalPages - 1) pages.push('ellipsis')

    pages.push(totalPages)

    return pages
  }

  const visiblePages = getVisiblePages()

  return (
    <div className="flex items-center justify-center gap-1 py-2">
      {/* Previous */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={disabled || currentPage <= 1}
        className="w-10 h-10 flex items-center justify-center rounded-xl text-on-surface-variant disabled:opacity-30 hover:bg-surface-container-high transition-colors"
        aria-label="Previous page"
      >
        <span className="material-symbols-outlined text-[20px]">chevron_left</span>
      </button>

      {/* Page numbers */}
      {visiblePages.map((p, i) =>
        p === 'ellipsis' ? (
          <span key={`e${i}`} className="w-8 h-10 flex items-center justify-center text-xs text-on-surface-variant">
            ...
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            disabled={disabled}
            className={`min-w-[36px] h-10 px-2 flex items-center justify-center rounded-xl text-sm font-bold transition-colors ${
              p === currentPage
                ? 'bg-primary text-on-primary shadow-sm'
                : 'text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            {p}
          </button>
        )
      )}

      {/* Next */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={disabled || currentPage >= totalPages}
        className="w-10 h-10 flex items-center justify-center rounded-xl text-on-surface-variant disabled:opacity-30 hover:bg-surface-container-high transition-colors"
        aria-label="Next page"
      >
        <span className="material-symbols-outlined text-[20px]">chevron_right</span>
      </button>
    </div>
  )
}

// ─── Compact Swipeable Card ───────────────────────────────────────

const CARD_ACCENT_COLORS = [
  'border-primary',        // Coral (#cc785c)
  'border-primary-fixed',  // Coral active (#a9583e)
] as const

interface SwipeableLibraryCardProps {
  card: Card
  colorIndex: number
  isMastered: boolean
  onView: () => void
  onDelete: () => void
}

function SwipeableLibraryCard({ card, colorIndex, isMastered, onView, onDelete }: SwipeableLibraryCardProps) {
  const [swipeState, setSwipeState] = useState<SwipeState>('normal')
  const [translateX, setTranslateX] = useState(0)
  const [viewBgOpacity, setViewBgOpacity] = useState(0)
  const [deleteBgOpacity, setDeleteBgOpacity] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const dragState = useRef({
    startX: 0,
    startY: 0,
    lastDx: 0,
    phase: 'idle' as 'idle' | 'deciding' | 'dragging' | 'cancelled',
  })
  const accentColor = CARD_ACCENT_COLORS[colorIndex % CARD_ACCENT_COLORS.length]

  const updateVisuals = useCallback((deltaX: number) => {
    const clampedX = Math.max(-100, Math.min(100, deltaX))
    setTranslateX(clampedX)

    const progress = Math.abs(clampedX) / SWIPE_THRESHOLD

    if (clampedX > 0) {
      setViewBgOpacity(Math.min(1, progress))
      setDeleteBgOpacity(0)
    } else if (clampedX < 0) {
      setDeleteBgOpacity(Math.min(1, progress))
      setViewBgOpacity(0)
    } else {
      setViewBgOpacity(0)
      setDeleteBgOpacity(0)
    }
  }, [])

  const resetCard = useCallback(() => {
    setTranslateX(0)
    setViewBgOpacity(0)
    setDeleteBgOpacity(0)
    setSwipeState('normal')
    setIsDragging(false)
  }, [])

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      lastDx: 0,
      phase: 'deciding',
    }
  }, [])

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const state = dragState.current
    if (state.phase === 'idle' || state.phase === 'cancelled') return

    const dx = e.clientX - state.startX
    const dy = e.clientY - state.startY

    if (state.phase === 'deciding') {
      const distance = Math.sqrt(dx * dx + dy * dy)
      if (distance > DIRECTION_LOCK_THRESHOLD) {
        if (Math.abs(dx) > Math.abs(dy)) {
          state.phase = 'dragging'
          setIsDragging(true)
          ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
          state.lastDx = dx
          updateVisuals(dx)
        } else {
          state.phase = 'cancelled'
        }
      }
      return
    }

    state.lastDx = dx
    updateVisuals(dx)
  }, [updateVisuals])

  const handlePointerUp = useCallback(() => {
    const state = dragState.current

    if (state.phase === 'dragging') {
      const dx = state.lastDx
      if (Math.abs(dx) >= SWIPE_THRESHOLD) {
        if (dx > 0) {
          setSwipeState('view')
          setTranslateX(60)
          onView()
        } else {
          setSwipeState('delete')
          setTranslateX(-60)
          onDelete()
        }
      }
    }
    resetCard()
    state.phase = 'idle'
  }, [onView, onDelete, resetCard])

  const borderColor = swipeState === 'view'
    ? 'border-primary-container'
    : swipeState === 'delete'
      ? 'border-secondary'
      : accentColor

  return (
    <div
      ref={containerRef}
      className="relative select-none"
    >
      {/* View Background (swipe right) */}
      <div
        className="absolute inset-y-0 left-0 w-full bg-primary-container flex items-center px-4 rounded-xl transition-opacity duration-200"
        style={{ opacity: viewBgOpacity }}
      >
        <span className="material-symbols-outlined text-xl text-on-primary-container">visibility</span>
        <span className="text-[10px] font-bold ml-1 text-on-primary-container">View</span>
      </div>

      {/* Delete Background (swipe left) */}
      <div
        className="absolute inset-y-0 right-0 w-full bg-error-container flex items-center justify-end px-4 rounded-xl transition-opacity duration-200"
        style={{ opacity: deleteBgOpacity }}
      >
        <span className="text-[10px] font-bold mr-1 text-on-error-container">Delete</span>
        <span className="material-symbols-outlined text-xl text-on-error-container">delete</span>
      </div>

      {/* Draggable Card */}
      <div
        className={`relative bg-surface-container-lowest p-4 flex items-center border border-hairline border-l-[4px] ${borderColor} rounded-xl cursor-grab active:cursor-grabbing touch-pan-y`}
        style={{
          transform: `translateX(${translateX}px)`,
          transition: isDragging ? 'transform 0.075s linear' : 'transform 0.3s ease-out',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => {
          dragState.current.phase = 'idle'
          resetCard()
        }}
      >
        <div className="flex-1 min-w-0">
            <h3 className="font-headline font-extrabold text-lg text-on-surface leading-tight">
              {card.front}
            </h3>
            {(card.phonetic_us || card.phonetic_uk) && (
              <div className="flex items-center gap-1 mt-0.5 mb-1 flex-wrap">
                {card.phonetic_us && (
                  <div className="bg-surface-container-low px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                    <span className="text-[9px] font-bold text-on-surface-variant">US {card.phonetic_us}</span>
                    <PronunciationButton text={getEnglishText(card.front, card.back)} accent="us" />
                  </div>
                )}
                {card.phonetic_uk && (
                  <div className="bg-surface-container-low px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                    <span className="text-[9px] font-bold text-on-surface-variant">UK {card.phonetic_uk}</span>
                    <PronunciationButton text={getEnglishText(card.front, card.back)} accent="uk" />
                  </div>
                )}
              </div>
            )}
          <p className="text-on-surface-variant text-xs leading-snug mb-1 line-clamp-2">
            {card.back}
          </p>
          {card.tags && card.tags.length > 0 && card.tags.some(t => t.includes(':')) && (
            <div className="flex flex-wrap gap-1 mb-1">
              {card.tags
                .filter(t => t.includes(':'))
                .slice(0, 3)
                .map(tag => (
                  <span key={tag} className="text-[8px] font-bold text-on-surface-variant bg-surface-container/50 px-1.5 py-0.5 rounded-full">
                    {tag.split(':')[1]}
                  </span>
                ))}
            </div>
          )}
          <span className="text-[9px] font-medium text-on-surface-variant/70">
            {new Date(card.created_at).toLocaleDateString()}
          </span>
        </div>
        <div className="flex flex-col items-center gap-0.5 ml-2 text-on-surface-variant/50">
          <span className="material-symbols-outlined text-[16px]">swipe</span>
          <span className="text-[7px] font-bold tracking-wide">DRAG</span>
        </div>
        {isMastered && (
          <span className="material-symbols-outlined text-primary text-[18px] absolute top-2 right-8" title="Mastered">verified</span>
        )}
      </div>
    </div>
  )
}

// ─── Expanded Detail Card ─────────────────────────────────────────

interface ExpandedCardProps {
  card: Card
  isEditing: boolean
  editedCard: Card | null
  savingId: string | null
  isMastered: boolean
  togglingMastered: boolean
  onEdit: () => void
  onCancelEdit: () => void
  onEditFieldChange: (field: keyof Card, value: string) => void
  onSaveEdit: () => void
  onCollapse: () => void
  onToggleMastered: () => void
}

function ExpandedCard({
  card,
  isEditing,
  editedCard,
  savingId,
  isMastered,
  togglingMastered,
  onEdit,
  onCancelEdit,
  onEditFieldChange,
  onSaveEdit,
  onCollapse,
  onToggleMastered,
}: ExpandedCardProps) {
  return (
    <div className="bg-surface-container-lowest rounded-2xl p-4 shadow-card border-l-[4px] border-primary-container animate-slide-down">
      {/* Collapse button */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={onCollapse}
          className="flex items-center gap-1 text-xs font-semibold text-on-surface-variant hover:text-on-surface transition-colors"
        >
          <span className="material-symbols-outlined text-[18px]">expand_less</span>
          Back
        </button>
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-on-surface-variant">Front</label>
            <input
              type="text"
              value={editedCard?.front || ''}
              onChange={e => onEditFieldChange('front', e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-surface-container-low rounded-xl text-on-surface text-base"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-on-surface-variant">Back</label>
            <input
              type="text"
              value={editedCard?.back || ''}
              onChange={e => onEditFieldChange('back', e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-surface-container-low rounded-xl text-on-surface text-base"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-on-surface-variant">Definition</label>
            <textarea
              value={editedCard?.definition || ''}
              onChange={e => onEditFieldChange('definition', e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-surface-container-low rounded-xl text-on-surface text-base resize-none"
              rows={2}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-on-surface-variant">Example</label>
            <textarea
              value={editedCard?.example || ''}
              onChange={e => onEditFieldChange('example', e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-surface-container-low rounded-xl text-on-surface text-base resize-none"
              rows={2}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={onSaveEdit}
              disabled={savingId === card.id}
              className="flex-1 py-2 bg-primary text-on-primary rounded-full font-semibold text-sm"
            >
              {savingId === card.id ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={onCancelEdit}
              className="flex-1 py-2 bg-surface-container text-on-surface rounded-full font-semibold text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          {getImageUrl(card.image_key) && (
            <img
              src={getImageUrl(card.image_key)!}
              alt={card.front}
              className="w-full max-h-28 object-contain rounded-2xl mb-3 bg-surface-container-low"
            />
          )}

          <p className="font-headline font-bold text-lg text-on-surface">{card.front}</p>

          {(card.phonetic_us || card.phonetic_uk) && (
            <div className="flex items-center gap-3 mt-1">
              {card.phonetic_us && (
                <span className="text-xs text-on-surface-variant flex items-center gap-1">
                  US {card.phonetic_us}
                  <PronunciationButton text={getEnglishText(card.front, card.back)} accent="us" />
                </span>
              )}
              {card.phonetic_uk && (
                <span className="text-xs text-on-surface-variant flex items-center gap-1">
                  UK {card.phonetic_uk}
                  <PronunciationButton text={getEnglishText(card.front, card.back)} accent="uk" />
                </span>
              )}
            </div>
          )}

          <p className="text-sm text-on-surface mt-1">{card.back}</p>

          {(card.definition || card.example) && (
            <div className="mt-2 pt-2">
              {card.pos && (
                <span className="inline-block px-2 py-0.5 bg-surface-container-low rounded-full text-xs font-bold text-on-surface-variant mb-1">
                  {card.pos}
                </span>
              )}
              {card.definition && (
                <p className="text-xs text-on-surface-variant">{card.definition}</p>
              )}
              {card.example && (
                <p className="text-xs text-on-surface-variant italic mt-1">&ldquo;{card.example}&rdquo;</p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-3 pt-2">
            <div className="flex items-center gap-2">
              <small className="text-xs text-on-surface-variant">
                {new Date(card.created_at).toLocaleDateString()}
              </small>
              <a
                href={`https://www.google.com/search?q=${encodeURIComponent(card.front)}&tbm=isch`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold text-on-surface-variant hover:text-primary hover:bg-primary/10 transition-colors"
                aria-label={`Search images for ${card.front}`}
              >
                <span className="material-symbols-outlined text-[16px]">image_search</span>
                Images
              </a>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onToggleMastered}
                disabled={togglingMastered}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors min-h-[44px] flex items-center gap-1 ${
                  isMastered
                    ? 'bg-surface-container text-on-surface-variant hover:bg-error/10 hover:text-error'
                    : 'bg-surface-container text-on-surface-variant hover:bg-primary/10 hover:text-primary'
                }`}
                aria-label={isMastered ? `Un-master ${card.front}` : `Mark ${card.front} as mastered`}
              >
                <span className="material-symbols-outlined text-[16px]">
                  {isMastered ? 'undo' : 'emoji_events'}
                </span>
                {togglingMastered ? '...' : isMastered ? <s>Mastered</s> : 'Master'}
              </button>
              <button
                onClick={onEdit}
                className="px-3 py-1.5 text-xs font-semibold text-primary rounded-full hover:bg-primary/10 transition-colors"
                aria-label={`Edit ${card.front} card`}
              >
                Edit
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
